// src/modules/Admin/pages/Blog/index.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../../config/supabase';
import {
  FaPlus,
  FaSearch,
  FaEdit,
  FaTrash,
  FaEye,
  FaEllipsisV,
  FaNewspaper
} from 'react-icons/fa';

// Styled Components
const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: #111;
`;

const CreateButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 20px;
  background: #111;
  color: white;
  text-decoration: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: background 200ms;

  &:hover {
    background: #333;
  }
`;

const FiltersBar = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap;
`;

const SearchInput = styled.div`
  position: relative;
  flex: 1;
  min-width: 200px;
  max-width: 400px;

  input {
    width: 100%;
    padding: 12px 16px 12px 44px;
    border: 1px solid #e5e5e5;
    border-radius: 8px;
    font-size: 14px;
    transition: all 200ms;

    &:focus {
      outline: none;
      border-color: #333;
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.05);
    }
  }

  svg {
    position: absolute;
    left: 16px;
    top: 50%;
    transform: translateY(-50%);
    color: #999;
    font-size: 16px;
  }
`;

const FilterSelect = styled.select`
  padding: 12px 16px;
  border: 1px solid #e5e5e5;
  border-radius: 8px;
  font-size: 14px;
  background: white;
  cursor: pointer;
  min-width: 150px;

  &:focus {
    outline: none;
    border-color: #333;
  }
`;

const Card = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 16px 20px;
  font-size: 12px;
  font-weight: 600;
  color: #666;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafafa;
`;

const Td = styled.td`
  padding: 16px 20px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
`;

const PostInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const PostThumbnail = styled.div`
  width: 64px;
  height: 48px;
  border-radius: 6px;
  background: #f0f0f0;
  background-image: ${props => props.$image ? `url(${props.$image})` : 'none'};
  background-size: cover;
  background-position: center;
  flex-shrink: 0;
`;

const PostDetails = styled.div`
  .title {
    font-size: 14px;
    font-weight: 600;
    color: #111;
    margin-bottom: 4px;
    max-width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .excerpt {
    font-size: 12px;
    color: #666;
    max-width: 300px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const StatusBadge = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 20px;
  background: ${props => {
    switch (props.$status) {
      case 'published': return 'rgba(16, 185, 129, 0.1)';
      case 'draft': return 'rgba(245, 158, 11, 0.1)';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.$status) {
      case 'published': return '#10b981';
      case 'draft': return '#f59e0b';
      default: return '#666';
    }
  }};
`;

const DateText = styled.span`
  font-size: 13px;
  color: #666;
`;

const ViewsText = styled.span`
  font-size: 13px;
  color: #666;
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #666;
  cursor: pointer;
  transition: all 200ms;

  &:hover {
    background: #f5f5f5;
    color: #111;
  }

  &.danger:hover {
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  color: #999;
  font-size: 32px;
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 8px;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const BlogList = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Erro ao buscar posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este post?')) return;

    try {
      const { error } = await supabase
        .from('posts')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setPosts(posts.filter(post => post.id !== id));
    } catch (error) {
      console.error('Erro ao excluir post:', error);
      alert('Erro ao excluir post. Tente novamente.');
    }
  };

  const filteredPosts = posts.filter(post => {
    const matchesSearch = post.title?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || post.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Container>
      <Header>
        <Title>Blog</Title>
        <CreateButton to="/admin/blog/novo">
          <FaPlus />
          Novo Post
        </CreateButton>
      </Header>

      <FiltersBar>
        <SearchInput>
          <FaSearch />
          <input
            type="text"
            placeholder="Buscar posts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SearchInput>
        <FilterSelect
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">Todos os status</option>
          <option value="published">Publicados</option>
          <option value="draft">Rascunhos</option>
        </FilterSelect>
      </FiltersBar>

      <Card>
        {loading ? (
          <EmptyState>
            <EmptyText>Carregando...</EmptyText>
          </EmptyState>
        ) : filteredPosts.length === 0 ? (
          <EmptyState>
            <EmptyIcon>
              <FaNewspaper />
            </EmptyIcon>
            <EmptyTitle>
              {posts.length === 0 ? 'Nenhum post ainda' : 'Nenhum post encontrado'}
            </EmptyTitle>
            <EmptyText>
              {posts.length === 0
                ? 'Comece criando seu primeiro post para o blog da Vega Robotics.'
                : 'Tente ajustar os filtros ou o termo de busca.'
              }
            </EmptyText>
            {posts.length === 0 && (
              <CreateButton to="/admin/blog/novo">
                <FaPlus />
                Criar primeiro post
              </CreateButton>
            )}
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Post</Th>
                <Th>Status</Th>
                <Th>Data</Th>
                <Th>Views</Th>
                <Th style={{ width: '100px' }}>Ações</Th>
              </tr>
            </thead>
            <tbody>
              {filteredPosts.map((post) => (
                <tr key={post.id}>
                  <Td>
                    <PostInfo>
                      <PostThumbnail $image={post.cover_image} />
                      <PostDetails>
                        <div className="title">{post.title}</div>
                        <div className="excerpt">{post.excerpt}</div>
                      </PostDetails>
                    </PostInfo>
                  </Td>
                  <Td>
                    <StatusBadge $status={post.status}>
                      {post.status === 'published' ? 'Publicado' : 'Rascunho'}
                    </StatusBadge>
                  </Td>
                  <Td>
                    <DateText>{formatDate(post.created_at)}</DateText>
                  </Td>
                  <Td>
                    <ViewsText>{post.views || 0}</ViewsText>
                  </Td>
                  <Td>
                    <ActionsCell>
                      {post.status === 'published' && (
                        <ActionButton
                          as="a"
                          href={`/blog/${post.slug}`}
                          target="_blank"
                          title="Ver post"
                        >
                          <FaEye />
                        </ActionButton>
                      )}
                      <ActionButton
                        as={Link}
                        to={`/admin/blog/editar/${post.id}`}
                        title="Editar"
                      >
                        <FaEdit />
                      </ActionButton>
                      <ActionButton
                        className="danger"
                        onClick={() => handleDelete(post.id)}
                        title="Excluir"
                      >
                        <FaTrash />
                      </ActionButton>
                    </ActionsCell>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </Container>
  );
};

export default BlogList;
